/**
 * useAgentData — agent lifecycle hook
 *
 * Encapsulates ALL agent-related state + API mutations that were previously
 * inlined inside ManagePlatform.js:
 *   • State: agentRequests, invitationCodes, approvedAgents, processingAgent,
 *            agentRequestFilter
 *   • Fetch: fetchAgentData() — pulls requests + codes + approved agents in parallel
 *   • Mutations: createInvitationCode, deleteInvitationCode, copyCode,
 *                approveAgentRequest, rejectAgentRequest, revokeAgent
 *   • Selector: getFilteredAgentRequests() — respects agentRequestFilter
 *
 * Extracted in iter-79 to keep ManagePlatform.js under control. All call sites
 * keep the same signatures — no behavioural change.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function useAgentData(token) {
  const [agentRequests, setAgentRequests] = useState([]);
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [approvedAgents, setApprovedAgents] = useState([]);
  const [processingAgent, setProcessingAgent] = useState(null);
  const [agentRequestFilter, setAgentRequestFilter] = useState('pending');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchAgentData = useCallback(async () => {
    if (!token) return;
    try {
      const [requestsRes, codesRes, agentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/agent-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/invitation-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/agents`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAgentRequests(requestsRes.data.data || []);
      setInvitationCodes(codesRes.data.data || []);
      setApprovedAgents(agentsRes.data.data || []);
    } catch (err) {
      console.log('Error fetching agent data:', err.message);
    }
  }, [token]);

  const createInvitationCode = async ({ maxUses, expiresInDays }) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/invitation-codes`,
        { max_uses: maxUses, expires_in_days: expiresInDays },
        { headers }
      );
      toast.success('تم إنشاء كود الدعوة');
      setInvitationCodes(prev => [res.data.data, ...prev]);
      return true;
    } catch {
      toast.error('فشل في إنشاء الكود');
      return false;
    }
  };

  const deleteInvitationCode = async (codeId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/invitation-codes/${codeId}`, { headers });
      toast.success('تم حذف الكود');
      setInvitationCodes(prev => prev.filter(c => c.id !== codeId));
    } catch {
      toast.error('فشل في حذف الكود');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('تم نسخ الكود');
  };

  // Accepts either the full request object (preferred — supplies user_id +
  // bgm_number to the backend in case the REST-API mode owns the row) or just
  // a string id (legacy local-DB-only mode).
  const approveAgentRequest = async (reqOrId) => {
    const req = (typeof reqOrId === 'object' && reqOrId !== null) ? reqOrId : { id: reqOrId };
    const requestId = req.id || req._id || req.requestId || req.request_id;
    if (!requestId) {
      toast.error('لا يوجد معرّف للطلب');
      return;
    }
    setProcessingAgent(requestId);
    try {
      const body = {
        user_id: req.user_id || req.userId || req.user?.id,
        bgm_number: req.bgm_number || req.bgmNumber || req.user?.bgm_number,
      };
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/agent-requests/${requestId}/approve`,
        body,
        { headers }
      );
      toast.success(`تمت الموافقة - رقم الوكيل: ${res.data.agent_id}`);
      await fetchAgentData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في الموافقة');
    } finally {
      setProcessingAgent(null);
    }
  };

  const rejectAgentRequest = async (req, reason) => {
    if (!req) return;
    const requestId = req.id || req._id || req.requestId || req.request_id;
    setProcessingAgent(requestId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/agent-requests/${requestId}/reject`,
        {
          reason,
          user_id: req.user_id || req.userId || req.user?.id,
        },
        { headers }
      );
      toast.success('تم رفض الطلب');
      await fetchAgentData();
      return true;
    } catch {
      toast.error('فشل في رفض الطلب');
      return false;
    } finally {
      setProcessingAgent(null);
    }
  };

  const revokeAgent = async (userId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/agents/${userId}/revoke`, {}, { headers });
      toast.success('تم إلغاء صفة الوكيل');
      await fetchAgentData();
    } catch {
      toast.error('فشل في إلغاء صفة الوكيل');
    }
  };

  const blockAgent = async (userId, reason = '') => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/agents/${userId}/block`, { reason }, { headers });
      toast.success('تم حظر الوكيل مؤقتاً');
      await fetchAgentData();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في حظر الوكيل');
      return false;
    }
  };

  const unblockAgent = async (userId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/agents/${userId}/unblock`, {}, { headers });
      toast.success('تم فك الحظر عن الوكيل');
      await fetchAgentData();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في فك الحظر');
      return false;
    }
  };

  const archiveAgent = async (userId, reason = '') => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/archive/agents/${userId}`, { reason }, { headers });
      toast.success('تم أرشفة الوكيل');
      await fetchAgentData();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في أرشفة الوكيل');
      return false;
    }
  };

  const getFilteredAgentRequests = () => {
    if (agentRequestFilter === 'all') return agentRequests;
    return agentRequests.filter(r => r.status === agentRequestFilter);
  };

  return {
    // state
    agentRequests, invitationCodes, approvedAgents,
    processingAgent, agentRequestFilter,
    // setters used by callers
    setAgentRequests, setInvitationCodes, setApprovedAgents,
    setAgentRequestFilter,
    // actions
    fetchAgentData,
    createInvitationCode, deleteInvitationCode, copyCode,
    approveAgentRequest, rejectAgentRequest, revokeAgent,
    blockAgent, unblockAgent, archiveAgent,
    getFilteredAgentRequests,
  };
}
